import { formatearFechaHora, type ModoDePartida, NOMBRE_MODO_DE_PARTIDA } from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { Modal } from "../componentes/ModalFirma.tsx";
import { Aviso, Boton, BotonEnlace, EstadoVacio, Pildora } from "../componentes/ui.tsx";
import { mensajeDe } from "../utiles.ts";

const MODOS: { modo: ModoDePartida; emoji: string; detalle: string }[] = [
  {
    modo: "en_vivo",
    emoji: "📺",
    detalle: "Proyectas las preguntas y todos responden a la vez, con tiempo y ranking.",
  },
  {
    modo: "a_tu_ritmo",
    emoji: "🧭",
    detalle: "Compartes el código y cada quien lo responde cuando pueda, sin reloj.",
  },
];

/** Mis retos, listos para jugarse, y el historial de partidas. */
export function PaginaQuices() {
  const trpc = useTRPC();
  const lista = useQuery(trpc.quices.listar.queryOptions());
  const [eligiendo, setEligiendo] = useState<{ id: string; titulo: string } | null>(null);

  return (
    <div>
      <Encabezado
        overline="Sistema"
        titulo="Reto SMG"
        acciones={
          <BotonEnlace to="/quices/nuevo" variante="primario">
            Nuevo reto
          </BotonEnlace>
        }
      />
      {lista.isPending && <EstadoVacio>Cargando…</EstadoVacio>}
      {lista.data && (
        <div className="space-y-7">
          <section>
            <h2 className="mb-2 text-[15px] font-bold text-marino">Mis retos</h2>
            {lista.data.quices.length === 0 ? (
              <EstadoVacio>Crea tu primer reto para jugarlo después de una expo. 🌶️</EstadoVacio>
            ) : (
              <ul className="divide-y divide-borde/60 rounded-tarjeta border border-borde bg-white">
                {lista.data.quices.map((q) => (
                  <li key={q.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{q.titulo}</div>
                      <div className="text-[11.5px] text-texto-3">
                        {q.preguntas === 1 ? "1 pregunta" : `${q.preguntas} preguntas`}
                        {!q.listo && " · le falta completarse"}
                      </div>
                    </div>
                    <BotonEnlace to="/quices/$id/editar" params={{ id: q.id }} compacto>
                      Editar
                    </BotonEnlace>
                    <Boton
                      compacto
                      variante="firma"
                      disabled={!q.listo}
                      onClick={() => setEligiendo(q)}
                    >
                      🎯 Jugar
                    </Boton>
                  </li>
                ))}
              </ul>
            )}
          </section>
          {lista.data.partidas.length > 0 && (
            <section>
              <h2 className="mb-2 text-[15px] font-bold text-marino">Partidas</h2>
              <ul className="divide-y divide-borde/60 rounded-tarjeta border border-borde bg-white">
                {lista.data.partidas.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{p.titulo}</div>
                      <div className="text-[11.5px] text-texto-3">
                        {NOMBRE_MODO_DE_PARTIDA[p.modo]} · {formatearFechaHora(p.creadaEn)} ·{" "}
                        {p.jugadores === 1 ? "1 participante" : `${p.jugadores} participantes`}
                      </div>
                    </div>
                    {p.terminada ? (
                      <Pildora tono="bg-marino-claro text-marino">Terminada</Pildora>
                    ) : (
                      <Pildora tono="bg-verde-fondo text-verde">En curso</Pildora>
                    )}
                    {!p.terminada && (
                      <BotonEnlace to="/partidas/$id/presentar" params={{ id: p.id }} compacto>
                        Volver a la partida
                      </BotonEnlace>
                    )}
                    <BotonEnlace to="/partidas/$id" params={{ id: p.id }} compacto>
                      Resultados
                    </BotonEnlace>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
      {eligiendo && <ModalJugar quiz={eligiendo} onCerrar={() => setEligiendo(null)} />}
    </div>
  );
}

function ModalJugar({
  quiz,
  onCerrar,
}: {
  quiz: { id: string; titulo: string };
  onCerrar: () => void;
}) {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const crear = useMutation(
    trpc.quices.crearPartida.mutationOptions({
      onSuccess: ({ id }) => {
        queryClient.invalidateQueries();
        navigate({ to: "/partidas/$id/presentar", params: { id } });
      },
      onError: (e) => setError(mensajeDe(e)),
    }),
  );

  return (
    <Modal titulo={`Jugar «${quiz.titulo}»`} onCerrar={onCerrar}>
      <div className="space-y-2">
        <p className="text-[13px] text-texto-2">¿Cómo quieres jugarlo?</p>
        {MODOS.map(({ modo, emoji, detalle }) => (
          <button
            key={modo}
            type="button"
            disabled={crear.isPending}
            onClick={() => crear.mutate({ quizId: quiz.id, modo })}
            className="block w-full rounded-md border border-borde bg-white px-3 py-2.5 text-left text-[13.5px] font-semibold text-marino hover:bg-marino-claro disabled:opacity-60"
          >
            {emoji} {NOMBRE_MODO_DE_PARTIDA[modo]}
            <span className="block text-[12px] font-normal text-texto-3">{detalle}</span>
          </button>
        ))}
        {error && <Aviso>{error}</Aviso>}
      </div>
    </Modal>
  );
}
